import { createFileRoute } from '@tanstack/react-router';
import { Button, Group, Skeleton, Stack, Table, TextInput, Title, Card, Text } from '@mantine/core';
import { IconSearch, IconReload } from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue } from '@mantine/hooks';
import { fetchList } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { EmptyState } from '@/shared/components/EmptyState';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { i18next } from '@/shared/i18n/config';

interface TranslationRow extends Denormalized {
  type: 'translations';
  id: string;
  key?: string;
  value?: string;
  language?: string;
  namespace?: string;
}

export const Route = createFileRoute('/_app/translations')({ component: TranslationsPage });

function TranslationsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['translations', debounced],
    queryFn: () =>
      fetchList<TranslationRow>('translations', {
        filter: debounced ? { key: { like: debounced } } : undefined,
        page: { size: 100 },
      }),
  });

  const rows = useMemo(() => data?.data ?? [], [data]);

  const patch = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      await httpRequest('/api/translations/patchVoice', {
        method: 'PATCH',
        body: { Id: id, Value: value },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['translations'] });
      notifications.show({ color: 'green', message: 'Traduzione aggiornata' });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore' }),
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Traduzioni</Title>
        <Button
          variant="light"
          leftSection={<IconReload size={16} />}
          onClick={() => void i18next.reloadResources()}
        >
          Ricarica nel client
        </Button>
      </Group>
      <TextInput
        placeholder="Cerca per chiave (es. auth.login)…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
      />
      <Card withBorder padding={0} radius="md">
        {isLoading ? (
          <Stack p="lg" gap="xs"><Skeleton h={24} /><Skeleton h={24} /><Skeleton h={24} /></Stack>
        ) : (
          <Table verticalSpacing="xs" horizontalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '30%' }}>Chiave</Table.Th>
                <Table.Th style={{ width: 100 }}>Lingua</Table.Th>
                <Table.Th>Valore</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">{t.key}</Text>
                  </Table.Td>
                  <Table.Td>{t.language ?? '—'}</Table.Td>
                  <Table.Td>
                    <EditableValue value={t.value ?? ''} onSave={(v) => patch.mutate({ id: t.id, value: v })} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!rows.length && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <EmptyState title="Nessuna traduzione" description="Prova ad allargare la ricerca o cambiare lingua." py="md" />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}

function EditableValue({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  if (!editing) {
    return (
      <Text
        size="sm"
        onClick={() => {
          setEditing(true);
          setV(value);
        }}
        style={{ cursor: 'pointer' }}
      >
        {value || <Text component="span" c="dimmed">vuoto — clicca per modificare</Text>}
      </Text>
    );
  }
  return (
    <Group gap="xs">
      <TextInput
        value={v}
        onChange={(e) => setV(e.currentTarget.value)}
        size="xs"
        style={{ flex: 1 }}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSave(v);
            setEditing(false);
          } else if (e.key === 'Escape') {
            setEditing(false);
          }
        }}
      />
      <Button size="xs" onClick={() => { onSave(v); setEditing(false); }}>Salva</Button>
      <Button size="xs" variant="subtle" onClick={() => setEditing(false)}>Annulla</Button>
    </Group>
  );
}
